import { Upload, type DetailedError } from "tus-js-client";
import { ApiError, readApiErrorResponse } from "@/lib/api-error";
import { touchSessionActivity } from "@/lib/session-activity";
import { apiFetch } from "@/services/http/api-fetch";

/**
 * A failure outside our API (the TUS transfer to Bunny). `code` is translated under
 * instructor.errors — SPA-6: an ApiError, so apiErrorText reads the code instead of falling back
 * to the generic "upload failed".
 */
export class VideoUploadFailed extends ApiError {
  constructor(code: string, message: string) {
    super(code, message, null);
    this.name = "VideoUploadFailed";
  }
}

type Ticket = {
  uploadId: string;
  tusEndpoint: string;
  title: string;
  headers: Record<string, string>;
};

/** C4: errors carry the API's code (ApiError), never just its English message. */
async function readData<T>(res: Response): Promise<T> {
  if (!res.ok) throw await readApiErrorResponse(res);
  return ((await res.json()) as { data: T }).data;
}

/**
 * CRS-8: a 4 GB upload over home broadband meets Wi-Fi drops and laptop sleeps. Back off to
 * 30 s and keep trying for about 35 minutes before giving up (was 0/3/10/30 s ≈ 45 s).
 */
export const TUS_RETRY_DELAYS_MS: number[] = [
  0,
  1_000,
  3_000,
  5_000,
  10_000,
  20_000,
  ...Array.from({ length: 68 }, () => 30_000),
];

/** The browser says there is no network (Wi-Fi dropped, laptop woke without it). */
function browserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** R-19: the upload was abandoned (sign-out, tab state reset); nothing restarts it. */
export class VideoUploadAborted extends Error {
  constructor() {
    super("Video upload aborted");
    this.name = "VideoUploadAborted";
  }
}

/**
 * R-19: resolves when the browser is back online (at once if it is online). The listener is
 * removed when the signal aborts, so an abandoned upload is never restarted by a later `online`.
 */
function whenOnline(onWaiting: ((waiting: boolean) => void) | undefined, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new VideoUploadAborted());
    if (!browserOffline()) return resolve();
    onWaiting?.(true);
    const onAbort = () => {
      window.removeEventListener("online", onOnline);
      reject(new VideoUploadAborted());
    };
    const onOnline = () => {
      signal?.removeEventListener("abort", onAbort);
      onWaiting?.(false);
      resolve();
    };
    window.addEventListener("online", onOnline, { once: true });
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * R-19: one of our API requests before the transfer (upload mode, the Bunny ticket). Offline, it
 * waits for the network and tries again instead of failing the upload; the ticket request is
 * safe to repeat (the API hands back the outstanding ticket for the same lesson + file, CRS-8).
 */
async function requestWhenOnline<T>(
  request: () => Promise<T>,
  onWaiting: ((waiting: boolean) => void) | undefined,
  signal?: AbortSignal,
): Promise<T> {
  for (;;) {
    await whenOnline(onWaiting, signal);
    try {
      return await request();
    } catch (err) {
      // A network failure (fetch rejects) while the browser says offline: wait and retry.
      if (!(err instanceof ApiError) && browserOffline()) continue;
      throw err;
    }
  }
}

/**
 * SPA-3: worth trying again — no response at all (network), Bunny overloaded or briefly busy.
 * tus's default also refuses every retry while the browser is offline, which turned a 20-second
 * Wi-Fi drop into a failed upload; offline is handled by waiting for the network instead.
 */
function retryableTusError(err: Error | DetailedError): boolean {
  if (!("originalRequest" in err) || err.originalRequest == null) return false;
  const status = err.originalResponse?.getStatus() ?? 0;
  return status === 0 || status >= 500 || status === 409 || status === 423 || status === 429;
}

/**
 * INFRA-007: when Bunny Stream is switched on, the browser uploads straight to Bunny over TUS
 * (resumable, never through this server) with headers the API signed; otherwise the file is posted
 * here as before. A Bunny upload is only accepted, not playable, when this resolves — Bunny still
 * has to encode it, which pollBunnyUpload watches.
 *
 * CRS-8: the API hands back the outstanding ticket for the same lesson + file, and the TUS
 * fingerprint names that Bunny video, so re-picking the file after a crash or a closed tab
 * resumes from the bytes Bunny already has instead of starting again.
 */
export async function uploadLessonVideoFile(
  lessonId: string,
  file: File,
  options: {
    onProgress?: (percent: number) => void;
    onTicket?: (uploadId: string) => void;
    /** SPA-3: true while the transfer is paused for the network, false when it carries on. */
    onWaitingForNetwork?: (waiting: boolean) => void;
    /** R-19: aborting stops the transfer and any wait for the network; the promise rejects. */
    signal?: AbortSignal;
  } = {},
): Promise<{ mode: "local" } | { mode: "bunny"; uploadId: string }> {
  const { onWaitingForNetwork: onWaiting, signal } = options;
  const mode = await requestWhenOnline(
    async () =>
      readData<{ provider: "local" | "bunny" }>(await apiFetch("/v1/instructor/video-upload-mode")),
    onWaiting,
    signal,
  );

  if (mode.provider === "local") {
    const body = new FormData();
    body.append("file", file);
    await readData(
      await apiFetch(`/v1/instructor/lessons/${lessonId}/video`, { method: "POST", body }),
    );
    return { mode: "local" };
  }

  const ticket = await requestWhenOnline(
    async () =>
      readData<Ticket>(
        await apiFetch(`/v1/instructor/lessons/${lessonId}/video/bunny-upload`, {
          method: "POST",
          body: JSON.stringify({ fileName: file.name, sizeBytes: file.size }),
        }),
      ),
    onWaiting,
    signal,
  );
  options.onTicket?.(ticket.uploadId);

  const videoId = ticket.headers.VideoId ?? ticket.uploadId;
  await new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new VideoUploadAborted());
    const upload = new Upload(file, {
      endpoint: ticket.tusEndpoint,
      headers: ticket.headers,
      metadata: { filetype: file.type || "video/mp4", title: ticket.title },
      retryDelays: TUS_RETRY_DELAYS_MS,
      // SPA-3: offline attempts would only burn the retry budget; see onError.
      onShouldRetry: (err) => retryableTusError(err) && !browserOffline(),
      // Tied to the Bunny video, so bytes are only ever resumed into the video they belong to.
      fingerprint: async (f: File) =>
        ["lms-bunny", videoId, f.name, f.size, f.lastModified].join(":"),
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onProgress: (sent, total) => {
        // A 4 GB upload runs for an hour with no clicks; bytes moving count as activity.
        touchSessionActivity();
        options.onProgress?.(Math.round((sent / total) * 100));
      },
      onSuccess: () => {
        signal?.removeEventListener("abort", abort);
        resolve();
      },
      onError: (err) => {
        // SPA-3: the Wi-Fi is down — pause, and carry on from the bytes Bunny already has (tus
        // asks the server for its offset) as soon as the browser is back online.
        if (browserOffline() && retryableTusError(err)) {
          // R-19: the wait ends with the signal; an abandoned upload is not restarted.
          whenOnline(onWaiting, signal).then(
            () => upload.start(),
            () => undefined,
          );
          return;
        }
        signal?.removeEventListener("abort", abort);
        reject(new VideoUploadFailed("TUS_FAILED", err.message));
      },
    });
    const abort = () => {
      void upload.abort();
      reject(new VideoUploadAborted());
    };
    signal?.addEventListener("abort", abort, { once: true });
    upload
      .findPreviousUploads()
      .then((previous) => {
        if (previous.length > 0) upload.resumeFromPreviousUpload(previous[0]!);
        upload.start();
      })
      .catch(() => upload.start());
  });
  return { mode: "bunny", uploadId: ticket.uploadId };
}

export type VideoUploadMode = "local" | "bunny" | "not_configured";

/**
 * Where new lesson videos go: the school's Bunny library (TUS from the browser), this server, or
 * nowhere yet (`not_configured`, FR-14 a). A 503 throws an ApiError carrying the API's code (F16).
 */
export async function fetchVideoUploadMode(): Promise<VideoUploadMode> {
  const data = await readData<{ provider: VideoUploadMode }>(
    await apiFetch("/v1/instructor/video-upload-mode"),
  );
  return data.provider;
}

export type BunnyUploadStatus = {
  status: "uploading" | "processing" | "ready" | "failed";
  error: string | null;
};

export async function pollBunnyUpload(
  lessonId: string,
  uploadId: string,
): Promise<BunnyUploadStatus> {
  const data = await readData<BunnyUploadStatus>(
    await apiFetch(`/v1/instructor/lessons/${lessonId}/video/uploads/${uploadId}`),
  );
  return { status: data.status, error: data.error };
}
